import { ReactElement, useEffect } from "react";
import { Box } from "@twilio-paste/core/box";
import { Button } from "@twilio-paste/core/button";
import { Tooltip } from "@twilio-paste/core/tooltip";
import {
  useLexicalComposerContext,
  CLEAR_EDITOR_COMMAND,
  KEY_ENTER_COMMAND,
  COMMAND_PRIORITY_HIGH,
  $getRoot,
  $createParagraphNode,
  $createTextNode,
} from "@twilio-paste/core/lexical-library";
import { SendIcon } from "@twilio-paste/icons/esm/SendIcon";

import { types } from "../definitions";
import {
  dataTestIds,
  ChatButtonToolType,
  WiseOwlApplicationEnum,
} from "@twiliointernal/e2-commons-shared-definitions";
import aispark from "../../../assets/aispark.svg";
import { copy } from "../definitions";

// A chat centric CTA that will clear the associated editor on click,
// and can be customized to respond to key enter events, customary of
// chat use-cases
const { pluginsCopy } = copy;

export const ChatSendButtonPlugin = ({
  isChatActive,
  loading,
  shouldClearInput,
  disabled = false,
  applicationId,
  handleOnClick,
  handleOnEnter,
  handleChatButtonToolClick,
}: types.ChatSendButtonPluginProps): ReactElement => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (handleOnEnter) {
      return editor.registerCommand(
        KEY_ENTER_COMMAND,
        (event: KeyboardEvent | null) => {
          const isValidEnterKeyEvent = event !== null && !event.shiftKey;
          if (isValidEnterKeyEvent && !loading && !disabled) {
            event.preventDefault();
            handleOnEnter();
          }

          return true;
        },
        COMMAND_PRIORITY_HIGH
      );
    }
  }, [handleOnEnter, isChatActive, loading, disabled]);

  /* Mitigates race condition where the lexical editor clears input before handleOnEnter
    can trigger (React Form state is cleared) - resulting in a validation error on submit.
    shouldClearInput is a boolean that needs to be set/reset externally. We're using React Form's
    form state to trigger this */
  useEffect(() => {
    if (shouldClearInput) {
      editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
    }
  }, [editor, shouldClearInput]);

  const handleSendMessage = (): void => {
    if (handleOnClick) {
      handleOnClick();
    }
  };

  const handlePromptTrigger = (): void => {
    if (handleChatButtonToolClick) {
      handleChatButtonToolClick(ChatButtonToolType.PROMPT_LIBRARY_TRIGGER);
      setComposerFocus();
    }
  };

  const setComposerFocus = (): void => {
    const composerEl = document.querySelector(
      `[data-testid="${dataTestIds.wiseOwlChat.chatComposer}"]`
    );
    if (composerEl) {
      (composerEl as HTMLElement).focus();
    }
  };

  return (
    <Box position="absolute" top="space50" right="space70">
      {applicationId && applicationId === WiseOwlApplicationEnum.wiseOwlApp && (
        <Box as="span" marginRight="space30">
          <Tooltip text={pluginsCopy.tooltip.promptLibrary}>
            <Button
              variant="primary_icon"
              size="reset"
              onClick={handlePromptTrigger}
              disabled={loading}
              data-testid={dataTestIds.wiseOwlChat.promptLibraryTrigger}
            >
              <img
                src={aispark}
                alt="Logo"
                color={loading ? "currentColor" : "colorTextPrimary"}
              />
            </Button>
          </Tooltip>
        </Box>
      )}
      <Button
        loading={loading}
        variant="primary_icon"
        size="reset"
        onClick={handleSendMessage}
        type="submit"
        disabled={!loading && disabled}
        data-testid={dataTestIds.wiseOwlChat.submitButton}
      >
        <SendIcon
          decorative={false}
          title="Send message"
          color={disabled ? "currentColor" : "colorTextPrimary"}
        />
      </Button>
    </Box>
  );
};

export const ChatForeslashCommandPlugin = ({
  applicationId,
  shouldDisplayPromptLibrary,
  handleDisplayPromptLibrary,
}: types.ChatForeslashCommandPluginProps) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        applicationId &&
        applicationId === WiseOwlApplicationEnum.wiseOwlApp &&
        event.key === "/"
      ) {
        const editorState = editor.getEditorState();
        let isEditorEmpty = true;

        // Inspect the editor state to determine if it is empty
        editorState.read(() => {
          const rootNode = editorState._nodeMap.get("root");
          isEditorEmpty = rootNode?.getTextContent()?.trim().length === 0;
        });

        if (isEditorEmpty && handleDisplayPromptLibrary) {
          handleDisplayPromptLibrary();
        }
      }
    };

    // Add the event listener to the editor's container
    const editorContainer = editor.getRootElement();
    if (editorContainer) {
      editorContainer.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (editorContainer) {
        editorContainer.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [shouldDisplayPromptLibrary, handleDisplayPromptLibrary, editor]);
  return null;
};

export const ChatUpdateInputPlugin = ({
  promptSuggestionSelected,
}: types.ChatUpdateInputPluginProps) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!promptSuggestionSelected) {
      return;
    }

    editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);

    editor.update(() => {
      const root = $getRoot();
      root.clear();

      const paragraphNode = $createParagraphNode();
      const textNode = $createTextNode(promptSuggestionSelected);
      paragraphNode?.append(textNode);
      root.append(paragraphNode);
    });
  }, [promptSuggestionSelected]);
  return null;
};
